import { useCallback, useEffect, useState } from 'react';
import {
  getAnswerQuotationByIdController,
  getMyAnswersController,
} from '../../controller';
import { AnswerQuotation } from '../../../../../../@types/interface';
import { useAppSelector } from '../../../../../../hooks/useAppSelector';

const LIMIT = 80;

export const useFinishedQuotations = () => {
  const { finishedQuotationList } = useAppSelector(
    state => state.answerQuotationReducer,
  );

  const [page, setPage] = useState(1);
  const [finishedQuotations, setFinishedQuotations] = useState<
    AnswerQuotation[]
  >(finishedQuotationList);
  const [isLoading, setIsLoading] = useState(false);
  const [noMoreData, setNoMoreData] = useState(false);
  const [selectedAnswerToView, setSelectedAnswerToView] =
    useState<AnswerQuotation | null>(null);
  const [isViewQuotationModalOpen, setIsViewQuotationModalOpen] =
    useState(false);

  const [searchedQuotation, setSearchedQuotation] =
    useState<AnswerQuotation | null>(null);

  const fetchFinishedAnswer = useCallback(async (id: string) => {
    const res = await getAnswerQuotationByIdController(id);

    if (res) {
      setSearchedQuotation(res);
    }
  }, []);

  const fetchNotAnsweredQuotations = useCallback(
    async (pageRequest: number) => {
      setIsLoading(true);
      if (!noMoreData) {
        const res = await getMyAnswersController({
          limit: LIMIT,
          page: pageRequest,
          option: 'finished',
        });

        if (res && res.length > 0) {
          setFinishedQuotations(previous => {
            const newAnswers = res.filter(
              answer =>
                !previous.some(
                  existingQuotation =>
                    answer.id_answer_quotation ===
                    existingQuotation.id_answer_quotation,
                ),
            );
            return [...previous, ...newAnswers];
          });
        } else {
          setNoMoreData(true);
        }
      }
      setIsLoading(false);
    },
    [noMoreData],
  );

  const loadMoreData = useCallback(
    (previousPage = 1) => {
      if (!noMoreData) {
        fetchNotAnsweredQuotations(previousPage);
        setPage(previousPage + 1);
      }
    },
    [fetchNotAnsweredQuotations, noMoreData],
  );

  const handleRowClick = (answer: AnswerQuotation) => {
    if (
      !selectedAnswerToView ||
      selectedAnswerToView.quotation_id !== answer.quotation_id
    ) {
      setSelectedAnswerToView(answer);
    } else {
      setSelectedAnswerToView(null);
    }
  };

  useEffect(() => {
    loadMoreData();
  }, [loadMoreData]);

  useEffect(() => {
    if (finishedQuotationList) {
      setFinishedQuotations(previous => {
        const newAnswers = finishedQuotationList.filter(
          answer =>
            !previous.some(
              existingQuotation =>
                answer.id_answer_quotation ===
                existingQuotation.id_answer_quotation,
            ),
        );
        return [...previous, ...newAnswers];
      });
    }
  }, [finishedQuotationList]);

  useEffect(() => {
    if (selectedAnswerToView && isViewQuotationModalOpen) {
      fetchFinishedAnswer(selectedAnswerToView.id_answer_quotation);
    }
  }, [fetchFinishedAnswer, isViewQuotationModalOpen, selectedAnswerToView]);

  return {
    page,
    loadMoreData,
    fetchNotAnsweredQuotations,
    selectedAnswerToView,
    setSelectedAnswerToView,
    isLoading,
    finishedQuotations,
    isViewQuotationModalOpen,
    setIsViewQuotationModalOpen,
    handleRowClick,
    searchedQuotation,
    setSearchedQuotation,
  };
};
